/**
 * Shaping Home's chats section, kept apart from `use-running-chats` so it stays
 * free of the auth and API-route imports the fetch needs — those reach into
 * Electron's `app` and can't be loaded in a unit test.
 */

import {
  normalizeConversationPreview,
  type ConversationPreviewPayload,
} from '@renderer/overlay/Sidepanel/conversation-preview'
import { isPreviewRevisionUnread } from './conversation-preview-acknowledgements'

/**
 * The subset of `GET /v1/conversations` rows this section reads.
 *
 * Declared here rather than reusing `ChatHistoryItem`: that's
 * `components['schemas']['Conversation']` off the generated
 * `backend-openapi.d.ts`, which predates the preview entirely — it has no
 * `preview` field to read. Regenerating it needs `pnpm gen:api-types` against a
 * gateway that has shipped the change.
 */
export interface SidepanelChatApiItem {
  id: string
  title?: string
  preview?: ConversationPreviewPayload | string | null
  createdAt: string
  updatedAt: string
  type?: string
  isStreaming?: boolean
  previewRevision?: number
  previewAcknowledgedRevision?: number
  metadata?: Record<string, unknown> | null
  /** All staged actions produced by the conversation, supplied by the For You surface. */
  stagedActionIds?: Array<string>
  audioNoteId?: string
}

export interface SidepanelChat {
  id: string
  title?: string
  conversationType?: string
  skillId?: string
  /** Latest completed assistant response, as the row's second line. */
  preview?: string
  stagedActionIds: Array<string>
  reminderIds: Array<string>
  audioNoteId?: string
  isStreaming: boolean
  previewRevision: number
  isUnread: boolean
  updatedAt: string
}

export const FOR_YOU_PREVIEW_LIMIT = 5

export interface ForYouChatPreviewSelection {
  chats: Array<SidepanelChat>
  hasMore: boolean
}

export function selectForYouChatPreview(
  chats: Array<SidepanelChat>,
  limit = FOR_YOU_PREVIEW_LIMIT,
): ForYouChatPreviewSelection {
  return {
    chats: chats.slice(0, limit),
    hasMore: chats.length > limit,
  }
}

export function countForYouUnreadChats(chats: Array<Pick<SidepanelChat, 'isUnread'>>): number {
  return chats.reduce((count, chat) => count + (chat.isUnread ? 1 : 0), 0)
}

export function isRegularChat(chat: Pick<SidepanelChat, 'conversationType'>): boolean {
  return chat.conversationType === 'chat'
}

export function shouldShowOutstandingActionCount(
  chat: Pick<SidepanelChat, 'conversationType'>,
  outstandingActionCount: number,
): boolean {
  return outstandingActionCount > 0 && (isRegularChat(chat) || outstandingActionCount > 1)
}

export const FROM_A_CHAT_LABEL = 'From a chat'

export function getForYouChatSubtitles(
  chat: Pick<SidepanelChat, 'conversationType'>,
  outstandingActionCount: number,
  needsInput: boolean,
  appearance: 'card' | 'todo' = 'card',
): Array<string> {
  const subtitles: Array<string> = []

  if (needsInput) subtitles.push('Needs input')
  if (shouldShowOutstandingActionCount(chat, outstandingActionCount)) {
    // A to-do's tags already show its draft, so a chat's lone action just names where it came from.
    subtitles.push(
      appearance === 'todo' && outstandingActionCount === 1
        ? FROM_A_CHAT_LABEL
        : `${outstandingActionCount} ${outstandingActionCount === 1 ? 'action' : 'actions'} to review`,
    )
  }

  return subtitles
}

export function isPreviewlessStreamingChat(chat: SidepanelChat): boolean {
  return (
    chat.isStreaming && chat.preview === undefined && chat.stagedActionIds.length === 0 && chat.reminderIds.length === 0
  )
}

export interface ForYouChatGroups {
  previewlessStreaming: Array<SidepanelChat>
  visibleStreaming: Array<SidepanelChat>
  actionable: Array<SidepanelChat>
}

export function groupForYouChats(chats: Array<SidepanelChat>): ForYouChatGroups {
  return {
    previewlessStreaming: chats.filter(isPreviewlessStreamingChat),
    visibleStreaming: chats.filter((chat) => chat.isStreaming && !isPreviewlessStreamingChat(chat)),
    actionable: chats.filter((chat) => !chat.isStreaming),
  }
}

interface FlowAnimationState {
  completedFlowIds: ReadonlySet<string>
  pendingFlowIds: Set<string>
}

export function deriveFlowAnimationState(
  chats: Array<SidepanelChat>,
  previousPendingFlowIds: ReadonlySet<string>,
): FlowAnimationState {
  return {
    completedFlowIds: new Set(
      chats
        .filter((chat) => !isPreviewlessStreamingChat(chat) && previousPendingFlowIds.has(chat.id))
        .map((chat) => chat.id),
    ),
    pendingFlowIds: new Set(chats.filter(isPreviewlessStreamingChat).map((chat) => chat.id)),
  }
}

function planItemSkill(metadata: Record<string, unknown> | null | undefined): string | undefined {
  const planItem = metadata?.planItem
  if (!planItem || typeof planItem !== 'object') return undefined
  const skill = (planItem as { skill?: unknown }).skill
  return typeof skill === 'string' && skill.length > 0 ? skill : undefined
}

function toSidepanelChat(row: SidepanelChatApiItem, fallbackPreviewRevision = 0): SidepanelChat {
  const preview = normalizeConversationPreview(row.preview)
  const previewRevision = row.previewRevision ?? fallbackPreviewRevision
  return {
    id: row.id,
    title: row.title ?? planItemSkill(row.metadata),
    conversationType: row.type,
    preview: preview.text,
    skillId: preview.skillId,
    stagedActionIds: Array.isArray(row.stagedActionIds)
      ? row.stagedActionIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
      : preview.stagedActionIds,
    reminderIds: preview.reminderIds,
    audioNoteId: row.audioNoteId,
    isStreaming: row.isStreaming === true,
    previewRevision,
    isUnread: isPreviewRevisionUnread(row.id, previewRevision, row.previewAcknowledgedRevision),
    updatedAt: row.updatedAt,
  }
}

export function buildSidepanelChats(rows: Array<SidepanelChatApiItem> | undefined): Array<SidepanelChat> {
  return (rows ?? []).map((row) => toSidepanelChat(row, 1))
}

export function applyOptimisticStreamingState(
  chats: Array<SidepanelChat>,
  locallyStreamingChatIds: ReadonlyArray<string>,
): Array<SidepanelChat> {
  if (chats.length === 0 || locallyStreamingChatIds.length === 0) return chats

  const locallyStreaming = new Set(locallyStreamingChatIds)
  let changed = false
  const next = chats.map((chat) => {
    if (chat.isStreaming || !locallyStreaming.has(chat.id)) return chat
    changed = true
    return { ...chat, isStreaming: true }
  })

  return changed ? next : chats
}

export function selectOutstandingActions<T extends { archivedAt?: string | null; outputData?: unknown }>(
  stagedActionIds: ReadonlyArray<string>,
  actionsById: ReadonlyMap<string, T>,
): Array<T> {
  return stagedActionIds.flatMap((actionId) => {
    const action = actionsById.get(actionId)
    return action && action.archivedAt == null && action.outputData == null ? [action] : []
  })
}
